import React from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';

const SalesCompositionCard = ({
  mode,
  title,
  chartLoading,
  chartData,
  COLORS,
  formatCurrency,
  formatNumber,
}) => {
  const isRetail = mode === 'retail';
  const data = isRetail ? chartData?.salesByStore : chartData?.salesByBranch;

  return (
    <Card className="flex h-full flex-col border border-slate-200/80 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="border-b border-slate-200 pb-2 text-base font-semibold text-slate-900">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex-1">
        {chartLoading ? (
          <div className="flex justify-center py-8 text-sm text-slate-500">Loading chart...</div>
        ) : data && data.length > 0 ? (
          isRetail ? (
            <div className="grid items-start gap-4 xl:grid-cols-[220px_minmax(0,1fr)]">
              <div>
                <ChartContainer config={{ value: { label: 'Sales Value' } }} className="h-[210px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data} cx="50%" cy="50%" innerRadius={48} outerRadius={74} stroke="rgba(255,255,255,0.95)" strokeWidth={1.5} labelLine={false} label={false} dataKey="value">
                        {data.map((entry, index) => <Cell key={`retail-cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />)}
                      </Pie>
                      <ChartTooltip
                        content={(
                          <ChartTooltipContent
                            hideLabel
                            formatter={(value, name, item) => (
                              <>
                                <span className="min-w-0 flex-1 truncate text-slate-600">{item?.payload?.name || name}</span>
                                <span className="font-mono font-medium tabular-nums text-slate-950">
                                  {formatCurrency(value)} ({item?.payload?.percentage ?? 0}%)
                                </span>
                              </>
                            )}
                          />
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartContainer>
                <div className="mt-1 px-1">
                  <p className="text-[10px] uppercase tracking-[0.08em] text-slate-500">Dominant Store</p>
                  <p className="text-sm font-semibold leading-[1.35] text-slate-800">
                    {data[0]?.name || '-'} berkontribusi {data[0]?.percentage || 0}% dari total sales.
                  </p>
                </div>
              </div>
              <ScrollArea className="h-[300px] rounded-xl border border-slate-200/80">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur supports-[backdrop-filter]:bg-slate-50/80">
                    <TableRow>
                      <TableHead className="h-8 text-[8px] uppercase tracking-[0.08em]">Store</TableHead>
                      <TableHead className="h-8 text-right text-[8px] uppercase tracking-[0.08em]">Transactions</TableHead>
                      <TableHead className="h-8 text-right text-[8px] uppercase tracking-[0.08em]">Revenue</TableHead>
                      <TableHead className="h-8 text-right text-[8px] uppercase tracking-[0.08em]">Share</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.map((store, index) => (
                      <TableRow key={store.name}>
                        <TableCell className="px-2 py-1.5">
                          <div className="flex items-start gap-1.5 leading-[1.1]">
                            <span
                              className="mt-0.5 h-2 w-2 shrink-0 rounded-[2px]"
                              style={{ backgroundColor: store.color || COLORS[index % COLORS.length] }}
                            />
                            <span className="text-[8px] font-medium text-slate-600">{store.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="px-2 py-1.5 text-right text-[8px] tabular-nums text-slate-600">{formatNumber(store.transactionCount || 0)}</TableCell>
                        <TableCell className="px-2 py-1.5 text-right text-[8px] font-semibold tabular-nums text-slate-600">{formatCurrency(store.value)}</TableCell>
                        <TableCell className="px-2 py-1.5 text-right text-[8px] font-medium tabular-nums text-slate-600">{store.percentage}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          ) : (
            <>
              <ChartContainer config={{ value: { label: 'Sales Value' } }} className="h-[280px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data} cx="50%" cy="45%" labelLine={false} label={false} outerRadius={100} dataKey="value">
                      {data.map((entry, index) => <Cell key={`avi-cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />)}
                    </Pie>
                    <ChartTooltip
                      content={(
                        <ChartTooltipContent
                          hideLabel
                          formatter={(value, name, item) => (
                            <>
                              <span className="min-w-0 flex-1 truncate text-slate-600">{item?.payload?.name || name}</span>
                              <span className="font-mono font-medium tabular-nums text-slate-950">
                                {formatCurrency(value)} ({item?.payload?.percentage ?? 0}%)
                              </span>
                            </>
                          )}
                        />
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </ChartContainer>
              <div className="mt-4 grid max-h-[300px] gap-2 overflow-y-auto">
                {data.map((branch, index) => (
                  <div key={branch.name} className="rounded-md border border-slate-200 bg-white p-3 transition-colors hover:bg-slate-50">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <span
                          className="h-5 w-5 shrink-0 rounded-sm"
                          style={{ backgroundColor: branch.color || COLORS[index % COLORS.length] }}
                        />
                        <div className="min-w-0">
                          <p className="break-words text-sm font-semibold text-slate-700">{branch.name}</p>
                          <p className="text-xs text-slate-500">{branch.transactionCount} transactions</p>
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-bold text-slate-800">{formatCurrency(branch.value)}</p>
                        <p className="text-xs font-medium text-slate-600">{branch.percentage}%</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )
        ) : (
          <p className="py-8 text-center text-sm text-slate-500">No data available</p>
        )}
      </CardContent>
    </Card>
  );
};

export default SalesCompositionCard;
